import type { MultiPolygon, Position } from 'geojson';

const METERS_PER_DEGREE = 111_320;
type Point = [number, number];
const isPoint = (point: Position): point is Point =>
  point.length >= 2 && point.every(Number.isFinite);

/** Round display-only footprint corners; stored geometry and camera bounds stay intact. */
export const roundProjectFootprint = (
  geometry: MultiPolygon,
  radiusMeters = 0.5,
): MultiPolygon => ({
  ...geometry,
  coordinates: geometry.coordinates.map((polygon) =>
    polygon.map((ring) => {
      if (ring.length < 4 || radiusMeters <= 0 || !ring.every(isPoint))
        return ring;
      const vertices = ring.slice(0, -1);
      const rounded: Point[] = [];
      for (const [index, corner] of vertices.entries()) {
        const previous =
          vertices[(index + vertices.length - 1) % vertices.length];
        const next = vertices[(index + 1) % vertices.length];
        if (!previous || !next) continue;
        const xScale =
          METERS_PER_DEGREE * Math.cos((corner[1] * Math.PI) / 180);
        const distance = (point: Point) =>
          Math.hypot(
            (point[0] - corner[0]) * xScale,
            (point[1] - corner[1]) * METERS_PER_DEGREE,
          );
        const previousDistance = distance(previous);
        const nextDistance = distance(next);
        if (!previousDistance || !nextDistance) {
          rounded.push(corner);
          continue;
        }
        const radius = Math.min(
          radiusMeters,
          previousDistance / 4,
          nextDistance / 4,
        );
        const start: Point = [
          corner[0] + ((previous[0] - corner[0]) * radius) / previousDistance,
          corner[1] + ((previous[1] - corner[1]) * radius) / previousDistance,
        ];
        const end: Point = [
          corner[0] + ((next[0] - corner[0]) * radius) / nextDistance,
          corner[1] + ((next[1] - corner[1]) * radius) / nextDistance,
        ];
        for (let step = 0; step <= 6; step++) {
          const t = step / 6;
          const curve = (a: number, b: number, c: number) =>
            (1 - t) ** 2 * a + 2 * (1 - t) * t * b + t ** 2 * c;
          rounded.push([
            curve(start[0], corner[0], end[0]),
            curve(start[1], corner[1], end[1]),
          ]);
        }
      }
      const first = rounded[0];
      return first ? [...rounded, [...first]] : ring;
    }),
  ),
});
